import React from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Paper,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SettingsApplications as PartsIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const CarComponentsStatus = ({ car, showComponents, setShowComponents }) => {
  const { t } = useTranslation('cars');

  return (
    <Paper elevation={3} sx={{ mb: 3 }}>
      <Box p={2}>
        <Box display="flex" alignItems="center" mb={2} onClick={() => setShowComponents(!showComponents)} sx={{ cursor: 'pointer' }}>
          <Box display="flex" alignItems="center">
            <PartsIcon color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">{t('carDetail.componentsStatus')}</Typography>
          </Box>
          <Box ml="auto">
            {showComponents ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Box>
        </Box>
        
        <Collapse in={showComponents}>
          <Grid container spacing={2}>
            {Object.entries(car.components || {}).map(([componentKey, component]) => {
              // Calculate remaining kilometers
              const usedKm = car.totalKilometers - component.lastReplaced;
              const remainingKm = Math.max(0, component.lifespan - usedKm);
              const usagePercentage = Math.min(100, Math.round((usedKm / component.lifespan) * 100));
              
              // Determine status color
              let statusColor = 'success.main';
              if (usagePercentage > 90) {
                statusColor = 'error.main';
              } else if (usagePercentage > 75) {
                statusColor = 'warning.main';
              } else if (usagePercentage > 50) {
                statusColor = 'info.main';
              }
              
              return (
                <Grid item xs={12} sm={6} md={6} key={componentKey}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                        {t(`carDetail.components.${componentKey}`)}
                      </Typography>
                      
                      <Box sx={{ position: 'relative', pt: 1, pb: 2 }}>
                        <Box sx={{ 
                          height: 8, 
                          borderRadius: 4, 
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}>
                          <Box 
                            sx={{ 
                              height: '100%', 
                              borderRadius: 4, 
                              width: `${usagePercentage}%`,
                              bgcolor: statusColor,
                              transition: 'width 1s ease-in-out'
                            }} 
                          />
                        </Box>
                      </Box>
                      
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            {t('carDetail.components.used')}:
                          </Typography>
                          <Typography variant="body2">
                            {usedKm.toLocaleString()} km
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            {t('carDetail.components.remaining')}:
                          </Typography>
                          <Typography variant="body2">
                            {remainingKm.toLocaleString()} km
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            {t('carDetail.components.lastReplaced')}:
                          </Typography>
                          <Typography variant="body2">
                            {component.lastReplaced.toLocaleString()} km
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" color="text.secondary">
                            {t('carDetail.components.lifespan')}:
                          </Typography>
                          <Typography variant="body2">
                            {component.lifespan.toLocaleString()} km
                          </Typography>
                        </Grid>
                        
                        {component.notes && (
                          <Grid item xs={12} sx={{ mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              {t('carDetail.components.notes')}:
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {component.notes}
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Collapse>
      </Box>
    </Paper>
  );
};

export default CarComponentsStatus; 